import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import Logo from './Logo';

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    
    const updateScrolled = () => {
      const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
      setScrolled(scrollY > 50);
      ticking = false;
    };
    
    const requestTick = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrolled);
        ticking = true;
      }
    };
    
    // Initial check
    updateScrolled();
    
    // Try multiple approaches to detect scroll
    window.addEventListener('scroll', requestTick);
    window.addEventListener('wheel', requestTick);
    window.addEventListener('touchmove', requestTick);
    
    // Also check periodically as a fallback
    const interval = setInterval(updateScrolled, 100);
    
    return () => {
      window.removeEventListener('scroll', requestTick);
      window.removeEventListener('wheel', requestTick);
      window.removeEventListener('touchmove', requestTick);
      clearInterval(interval);
    };
  }, []);
  
  useEffect(() => {
    // Reset scroll state when route changes
    setScrolled(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/menu', label: t('nav.menu') },
    { path: '/gallery', label: t('nav.gallery') },
    { path: '/location', label: t('nav.location') },
  ];

  return (
    <nav 
      className={`fixed w-full z-40 transition-all duration-500 ${
        scrolled 
          ? 'bg-red-50/90 backdrop-blur-md shadow-xl py-4 border-b border-red-100' 
          : 'bg-black backdrop-blur-sm py-5 border-b border-gray-800'
      }`}>
      <div className="section-padding">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center group">
            <motion.div 
              key={location.pathname}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative flex items-center"
            >
              {/* Brand Text */}
              <div className="relative">
                <h1 className="text-3xl font-display tracking-wider">
                  <span className={`bg-gradient-to-r ${
                    scrolled 
                      ? 'from-koi-red via-red-400 to-koi-red' 
                      : 'from-red-300 via-red-200 to-red-300'
                  } bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient-shift`}>
                    ROYAL O'NARA
                  </span>
                </h1>
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-koi-red transition-all duration-300 group-hover:w-full" />
              </div>
            </motion.div>
          </Link>

          <div className="hidden lg:flex items-center gap-10 ml-12">
            {navItems.map((item, index) => (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={item.path}
                  className={`relative font-body text-lg tracking-wide transition-colors duration-300 ${
                    scrolled ? 'text-gray-700 hover:text-koi-red' : 'text-white hover:text-red-300'
                  } ${location.pathname === item.path ? scrolled ? 'text-koi-red' : 'text-red-300' : ''}`}
                >
                  <span className="relative">
                    {item.label}
                    <span className={`absolute -bottom-2 left-0 w-full h-0.5 bg-red-500 transform transition-transform duration-300 ${
                      location.pathname === item.path ? 'scale-x-100' : 'scale-x-0'
                    } group-hover:scale-x-100`} />
                  </span>
                </Link>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="ml-8"
            >
              <LanguageSelector scrolled={scrolled} />
            </motion.div>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden text-2xl transition-colors duration-300 ${
              scrolled ? 'text-gray-700' : 'text-white'
            }`}
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white shadow-2xl border-t border-koi-red/20"
          >
            <div className="section-padding py-6">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block py-4 text-lg font-body tracking-wide text-gray-700 hover:text-koi-red transition-colors ${
                      location.pathname === item.path ? 'text-koi-red' : ''
                    }`}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 }}
                className="pt-4 border-t border-koi-red/20"
              >
                <LanguageSelector scrolled={true} />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;